import fetch from 'isomorphic-unfetch';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import styles from './newsletter-form.module.scss';
import PhoneModelSelect from '../PhoneModelSelect/PhoneModelSelect';
import ReCAPTCHA from 'react-google-recaptcha';
import { site_captcha_key } from '../../constants';
import { CheckmarkIcon, useBreakpoint } from '@buzzfeed/react-components';
import { captureException } from '@sentry/nextjs';
import { isAndroidAgent } from '@buzzfeed/bf-utils';

const validEmailRegExpr = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
const isValidEmail = email => validEmailRegExpr.test(email);

const NewsletterFormSpinner = () => <div className={styles.spinner}><span>Loading</span></div>;
const NewsletterFormError = ({ children }) => <p id='newsletter-error' className={styles.error} role="alert">{children}</p>;
const NewsletterFormSuccess = ({ children }) => <div id='newsletter-success' className={styles.success} role="alert"><CheckmarkIcon className={styles.checkmarkIcon} aria-hidden={true} title="submit" /> {children}</div>;

function NewsletterForm({ onSubmitSuccess }) {
  const recaptchaRef = useRef();
  const [emailAddress, setEmailAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneModel, setPhoneModel] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isAndroidUser, setIsAndroidUser] = useState(false);
  const isMobile = useBreakpoint('(max-width: 40rem)');

  useEffect(() => {
    setIsAndroidUser(isAndroidAgent());
  }, []);

  const validateForm = () => {
    if (!isValidEmail(emailAddress)) {
      return 'Please enter a valid email';
    }
    if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
      return 'Please enter a valid phone number';
    }
  }

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (submitting) { return; }
    setSuccess(false);

    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }

    const token = await recaptchaRef.current.executeAsync();
    // reset allows for subsequent submissions without error.
    recaptchaRef.current.reset();

    setSubmitting(true);
    const isAndroid = isMobile ? isAndroidUser : phoneModel === 'android';
    const subscriptions = isAndroid ? ['buzzfeed_email_island_android'] : ['buzzfeed_email_island'];
    const body = {
      brand: 'buzzfeed',
      subscriptions,
      source: 'buzzfeed-island',
      'g-recaptcha-response': token,
      email: emailAddress,
      phone_number: phoneNumber
    };

    const response = await fetch('/newsletters/api/subhub/v1/users', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      }
    });
    setSubmitting(false);

    if (!response.ok) {
      captureException(new Error(`Newsletter signup failed with status ${response.status}`));
      return setError('Oof, something broke on our side. Please try again later.');
    }

    setError(null);
    setSuccess(true);
    onSubmitSuccess();
    sessionStorage.setItem('newsletterAddressable', 'true');
  };

  return (
    <form className={`${styles.form} ${isAndroidUser ? styles.androidForm : ''}`}>
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={site_captcha_key} style={{ visibility: 'hidden' }} />
      {!success && (
        <fieldset className={`${styles.fieldset} ${submitting ? styles.loading : ''}`}>
          <legend className={styles.legend}>Island signup form</legend>
          <div className={styles.submitBox}>
            <div className={styles.inputsGroup}>
              <div className={`${styles.emailContainer} ${isValidEmail(emailAddress) ? styles.valid : ''}`}>
                <input
                  className={`${styles.input} ${styles.emailInput}`}
                  id='newsletter-email-signup'
                  type="email"
                  name="email"
                  value={emailAddress}
                  onChange={e => setEmailAddress(e.target.value)}
                  autoComplete="email"
                  placeholder="Your email"
                  aria-label="Email Address"
                  aria-describedby={error ? 'newsletter-email-signup-error' : success ? 'newsletter-success' : null}
                  size={15}
                  required
                />
                <label className={styles.label} htmlFor='newsletter-email-signup'>Email Address</label>
                <CheckmarkIcon className={styles.checkIcon} />
              </div>
              <div className={`${styles.phoneContainer} ${phoneNumber && isValidPhoneNumber(phoneNumber) ? styles.valid : ''}`}>
                <PhoneInput
                  placeholder="Your phone number"
                  defaultCountry='US'
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  className={styles.input}
                />
                <label className={styles.label} htmlFor='newsletter-phone-signup'>Phone Number</label>
                <CheckmarkIcon className={styles.checkIcon} />
              </div>
              <div className={styles.phoneModelContainer}>
                <PhoneModelSelect value={phoneModel} onChange={setPhoneModel} />
              </div>
            </div>
            <div className={styles.buttonRow}>
              {(() => {
                const emailValid = isValidEmail(emailAddress);
                const phoneValid = !phoneNumber || isValidPhoneNumber(phoneNumber);
                const phoneModelChosen = !!phoneModel;
                // Mobile: only email required. Desktop: email + phone model.
                const allValidDesktop = emailValid && phoneValid && phoneModelChosen;
                const allValidMobile = emailValid && phoneValid; // phone model optional on mobile
                const canActivate = (isMobile ? allValidMobile : allValidDesktop) && !success;
                return (
                  <button
                    className={`${styles.button} ${canActivate ? styles.buttonActive : ''}`}
                    type="submit"
                    onClick={handleFormSubmit}
                    disabled={submitting || success || !canActivate}
                    aria-disabled={submitting || success || !canActivate}
                  >
                    <span className={styles.buttonLabel}>Sign up</span>
                  </button>
                );
              })()}
            </div>
          </div>
          {submitting && <NewsletterFormSpinner />}
          {error && (<NewsletterFormError>{error}</NewsletterFormError>)}
        </fieldset>
      )}
      {success && (
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Island signup form</legend>
          <div className={`${styles.submitBox} ${styles.successBox}`}>
            <div className={styles.inputsGroup}>
              <NewsletterFormSuccess>Success! We&apos;ll be in touch.</NewsletterFormSuccess>
            </div>
          </div>
        </fieldset>
      )}
    </form>
  );
}

NewsletterForm.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  onSubmitSuccess: PropTypes.func,
};

export default NewsletterForm;
